import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form945Instructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 945 Step-by-step filing instructions | TaxZerone ®"
          description="Form 945, Annual Return of Withheld Federal Income Tax is an important form to be filed by Businesses. Here are the step-by-step instructions to file form 945"
          keywords=""
          canonical="resources/employment-tax/form-945-instructions/"
          ogType="article"
          jsonLdPricing="6.99"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="54869"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 945</span>
          </span>{' '}
          Instructions
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/employment-tax/form-945-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-06-15T09:00:00-05:00"
                      >
                        June 15, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-06-15T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        June 15, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Nikhil Chowdary
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },

                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 945 at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        Form 945 ({' '}
                        <span style={{ fontWeight: 600 }}>
                          Annual Return of Withheld Federal Income Tax
                        </span>
                        ) is filed with the IRS by businesses to report{' '}
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          federal income tax withheld
                        </span>{' '}
                        from{' '}
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          nonpayroll payments
                        </span>
                        , such as pensions, annuities, IRAs, gambling winnings,
                        military retirement, and backup withholding.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <Link
                          href="/resources/employment-tax/form-945-due-dates/"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          Due Date
                        </Link>{' '}
                        :Form 945 is filed{' '}
                        <span style={{ fontWeight: 600 }}> annually</span> and
                        is generally due by{' '}
                        <span style={{ fontWeight: 600 }}> January 31</span> for
                        the previous calendar year.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <span style={{ fontWeight: 600 }}> Penalties:</span>{' '}
                        Failure to file, pay, or deposit withheld taxes on time
                        may result in
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          IRS penalties and interest
                        </span>
                        .
                      </span>
                    </li>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  <Link
                    href="/employment-tax/e-file-irs-form-945/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    IRS Form 945
                  </Link>{' '}
                  is an Annual Return of Withheld Federal Income Tax form.
                  Businesses need to file this form to report the nonpayroll
                  payments made by the employer during the tax year and the tax
                  withheld from these nonpayroll payments.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who must file Form 945?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Businesses withholding taxes from the nonpayroll payment
                  should file form 945 to the Internal Revenue Service. You may
                  need to file Form 945-A if you are a semiweekly depositor of
                  the taxes.
                </Typography>
                <Alert
                  icon={<TipsAndUpdatesOutlinedIcon />}
                  severity="info"
                  sx={{ mt: 2, mb: 2 }}
                >
                  <b>New announcement :</b>
                  <Typography variant="body1" sx={{ fontSize: 15 }}>
                    IRS is now supporting the electronic filing of Form 945-X,
                    the adjusted annual return of Form 945, and also
                    facilitating electronic claims for refund.{' '}
                  </Typography>
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Important dates
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The deadline for filing Form 945 is <b>January 31st</b> of
                  every year. If the deadline falls on a weekend or holiday, it
                  is extended to the following working day. If you paid the full
                  taxes for the year on time, you can file the return by
                  February 10, 2026, for the tax year 2025.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Filing methods supported by Form 945
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1 } }}>
                  <li>
                    <b>E-Filing:</b> The IRS recommends Employers file form 945,
                    Annual Return of Withheld Federal Income Tax to file using
                    the Electronic filing method.
                  </li>
                  <li>
                    <b>Paper filing:</b> The Employer can file form 945 using
                    the paper filing method by completing the form and mailing
                    it to the respective address based on the state from where
                    you are filing.
                  </li>
                </Box>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="body1">
                        TaxZerone recommends E-filing of Form 945 for easy
                        filing and faster processing of your employment forms
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 150, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=945instructions`}
                        id="where"
                      >
                        E-File Today
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Important information required in Form 945
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  To file Form 945, below is the information typically required:
                </Typography>
                <Box component="ol" sx={{ mb: 3, '& li': { mb: 1 } }}>
                  <li>Business information such as Name, EIN, and Address</li>
                  <li>Federal income tax withheld </li>
                  <li>Backup withholding</li>
                  <li>Total taxes</li>
                  <li>Total deposits</li>
                  <li>Balance due</li>
                  <li>Overpayment</li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What’s New for 2025
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Direct Deposit for Refunds:
                    </span>{' '}
                    Under{' '}
                    <Link
                      href="https://www.federalregister.gov/documents/2025/03/28/2025-05522/modernizing-payments-to-and-from-americas-bank-account"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Executive Order 14247
                    </Link>{' '}
                    issued on March 25, 2025, Form 945 refunds can now be issued
                    by direct deposit, offering a faster and more secure option.
                    You may still choose to apply any overpayment to your next
                    return (see line 6b).{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Electronic Payments Required:
                    </span>{' '}
                    Following Executive Order 14247, all Form 945 balance due
                    payments must now be made electronically.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Online Return Transcripts:
                    </span>{' '}
                    <Link
                      href="https://www.irs.gov/businesstranscript"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 945 return transcripts
                    </Link>{' '}
                    for tax year 2023 and later are available through your IRS
                    Business Tax Account at
                    <Link
                      href="https://www.irs.gov/BusinessAccount"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      IRS.gov/BusinessAccount
                    </Link>
                    .{' '}
                  </li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Step-by-step instructions to file form 945
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    mt: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Step 1: Enter the employer details
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f945-employee-img.webp" />
                  <source srcSet="/assets/images/f945-employee-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f945-employee-img.png"
                    loading="lazy"
                    alt="Form 945: Enter the EIN, Name, Complete address, and return type."
                    width={760}
                    height={374}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Box component="ul" sx={{ mb: 2 }}>
                  <li style={{ marginBottom: 16 }}>
                    <span style={{ fontWeight: 600 }}>
                      Employer identification number (EIN):{' '}
                    </span>{' '}
                    Enter the Unique EIN of your Business{' '}
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <span style={{ fontWeight: 600 }}> Name:</span> Provide the
                    legal Business name
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <span style={{ fontWeight: 600 }}>Address:</span> Fill in
                    your Business address such as Street name, City, State or
                    Province name, and ZIP code.
                  </li>
                  <li>
                    If the address is different from prior return, check the box
                    on right side of the form.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  <span style={{ fontWeight: 600 }}> Line A:</span> If this is
                  the final return (you will not need to file again because
                  you’ve stopped withholding non-payroll tax), check the box on
                  line A and provide the date of final payment.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Step 2: Enter tax withheld
                </Typography>

                <picture>
                  <source srcSet="/assets/images/f945-tax-img.webp" />
                  <source srcSet="/assets/images/f945-tax-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f945-tax-img.png"
                    loading="lazy"
                    alt="Form 945: Enter the EIN, Name, Complete address, and return type. "
                    width={760}
                    height={358}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Line 1:</span> Enter the
                  total amount of tax withheld from pensions, annuities, IRAs,
                  Military retirement, Gambling winnings, Indian gaming profits,
                  etc., during the tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 2:</span> Enter the
                  backup withholding amount for a full year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 3:</span> Add the
                  amount that you entered on Line 1 and Line 2. Now, on Line 3,
                  enter the total amount.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 4:</span> Enter the
                  total deposit amount for 2025, including overpayments from
                  Form 945-X and overpayments from a previous year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 5:</span> There is a
                  balance due if Line 3 exceeds Line 4.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 6a:</span> If Line 4
                  is greater than Line 3, enter the difference on line 6a.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 6b:</span> If you have
                  an overpayment, select whether you want the amount{' '}
                  <span style={{ fontWeight: 600 }}>
                    applied to your next return
                  </span>{' '}
                  or as a <span style={{ fontWeight: 600 }}>refund</span>.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 6c:</span>Enter the
                  routing number
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Line 6d:</span> Check the
                  account type – checking or savings
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}> Line 6e:</span> Enter the
                  account number
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Step 3: Select the Depositor Category
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f945-depositor-category-img.webp" />
                  <source srcSet="/assets/images/f945-depositor-category-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f945-depositor-category-img.png"
                    loading="lazy"
                    alt="Form 945: Select the check box based on your deposit schedule, and complete Form 945-A. If Line 3 is under $2,500, no additional details are needed. "
                    width={760}
                    height={60}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 3 }}>
                  <li style={{ marginBottom: 8 }}>
                    Select the first check box, if you are a semi-weekly
                    depositor and enter details in <br />
                    form 945-A
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Select the second check box, if you are a monthly scheduled
                    depositor and enter <br />
                    details in 7
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    If your Line 3 is less than $2500, there is no need to fill
                    up the Line 7 or Form 945-A.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Step 4: Enter the details in line 7 if you are a monthly
                  Depositor
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f945-monthly-depositor-img.webp" />
                  <source srcSet="/assets/images/f945-monthly-depositor-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f945-monthly-depositor-img.png"
                    loading="lazy"
                    alt="Form 945 Line 7: Enter the monthly summary of federal tax liability and also the total liability on line 7m."
                    width={716}
                    height={200}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 3 }}>
                  <li style={{ marginBottom: 16 }}>
                    Enter the total tax withheld for the month of January(7a),
                    February(7b), March(7c), April(7d), May(7e), June(7f),
                    July(7g), August(7h), September(7i), October(7j),
                    November(7k), and December(7l)
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    (7m) Add the amount you entered from Line 7a to 7l. You can
                    now put the total amount of taxes withheld for the year in
                    Line 7m. Verify that the total amounts on Lines 3 and 7m are
                    equal.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Step 5: Enter the details in form 945-A if you are a
                  semi-weekly Depositor
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f945a-semi-weekly-img.webp" />
                  <source srcSet="/assets/images/f945a-semi-weekly-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f945a-semi-weekly-img.png"
                    loading="lazy"
                    alt="Form 945: Enter the daily tax liability for January through June and the total for each month."
                    width={760}
                    height={601}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 2 }}>
                  <li style={{ marginBottom: 16 }}>
                    Enter the total tax withheld from the employee&apos;s wages
                    for each day of the month from January to June.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Enter the total tax withheld in each month in the respective
                    fields
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/f945a-tax-total-img.webp" />
                  <source srcSet="/assets/images/f945a-tax-total-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f945a-tax-total-img.png"
                    loading="lazy"
                    alt="Form 945: Enter the daily tax liability for July through December and the total for each month. Also, add the total liability of lines A to L."
                    width={760}
                    height={640}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 3 }}>
                  <li style={{ marginBottom: 16 }}>
                    Enter the total tax withheld from the employee&apos;s wages
                    for each day of the month from July to December.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Enter the total tax withheld in each month in the respective
                    fields
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Add the taxes mentioned from line A to L and enter the total
                    taxes in line M. The Tax amount should be equal to the taxes
                    mentioned in M and should be equal to line 3 of Form 945
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Step 6: Third-Party Designee
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f945-third-party-img.webp" />
                  <source srcSet="/assets/images/f945-third-party-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f945-third-party-img.png"
                    loading="lazy"
                    alt="Form 945: Enter the third-party designee's name, phone number, and PIN."
                    width={739}
                    height={156}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you want to allow an employee/tax preparer/another person
                  to discuss your return with the IRS, check{' '}
                  <span style={{ fontWeight: 600 }}>“Yes”</span> and enter the
                  designee’s name, phone number, and a 5-digit PIN. Otherwise,
                  check{' '}
                  <span style={{ fontWeight: 600 }}> &quot;No&quot;.</span>
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.5,
                  }}
                >
                  Step 6: Sign the form
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f945-sign-form-img.webp" />
                  <source srcSet="/assets/images/f945-sign-form-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f945-sign-form-img.png"
                    loading="lazy"
                    alt="Form 945: Enter your Sign, date, and best daytime to make a call."
                    width={751}
                    height={164}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 1 }}>
                  <li style={{ marginBottom: 16 }}>
                    The authorized person to file the return should sign the
                    form along with their name. Also, enter the name, title,
                    date, and mention the best daytime to talk with the IRS.
                  </li>
                </Box>

                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    // defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided is based on guidelines from the
                        IRS website. For more information, check the
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i945.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          IRS instructions
                        </Link>{' '}
                        for Form 945.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              E file form 945 with Taxzerone
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              E-File Form 945 easily with Taxzerone for just <b>$6.99 per</b>{' '}
              return.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              Start your Form 945 E-filing process with us in 3 simple steps.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=945instructions`}
              >
                E-file Form 945 Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form945Instructions;
